import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from '../services/api';

interface GalleryItem {
  id: number;
  title: string;
  description?: string;
  category: string;
  imageUrl: string;
  isActive: boolean;
  createdAt: string;
}

interface GalleryResponse {
  images: GalleryItem[];
  totalPages: number;
  currentPage: number;
  total: number;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  galleryHeroImage?: string;
  isActive: boolean;
}

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  


  const fetchGalleryImages = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });
      
      const response: GalleryResponse = await ApiService.request(`/gallery?${queryParams}`);
      setGalleryItems(response.images);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
    } catch (err) {
      setError('Gallery images could not be loaded');
      console.error('Gallery fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

     useEffect(() => {
     fetchGalleryImages(1);
     loadHomeSettings();
   }, []);

   // Keyboard navigation for modal
   useEffect(() => {
     const handleKeyDown = (event: KeyboardEvent) => {
       if (selectedImage !== null) {
         if (event.key === 'ArrowLeft' && selectedImage > 0) {
           setSelectedImage(selectedImage - 1);
         } else if (event.key === 'ArrowRight' && selectedImage < galleryItems.length - 1) {
           setSelectedImage(selectedImage + 1);
         } else if (event.key === 'Escape') {
           setSelectedImage(null);
         }
       }
     };

     document.addEventListener('keydown', handleKeyDown);
     return () => document.removeEventListener('keydown', handleKeyDown);
   }, [selectedImage, galleryItems.length]);

  // Debug için ekleyin
  useEffect(() => {
    console.log('Gallery items:', galleryItems);
    galleryItems.forEach(item => {
      const imageUrl = getImageUrl(item.imageUrl);
      console.log('Original URL:', item.imageUrl);
      console.log('Processed URL:', imageUrl);
    });
  }, [galleryItems]);



  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  const getImageUrl = (imageUrl: string) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
    }
    if (imageUrl.startsWith('/uploads/')) {
      // /uploads/image.jpg -> /api/proxy/uploads/image.jpg
      const filename = imageUrl.replace('/uploads/', '');
      return `http://localhost:5000/api/proxy/uploads/${filename}`;
    }
    return imageUrl;
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="mt-4 text-gray-400 font-light">Loading gallery...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black text-white min-h-screen py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-400 font-light">{error}</p>
            <button 
              onClick={() => fetchGalleryImages(currentPage)}
              className="mt-4 px-6 py-3 border border-gray-600 text-white hover:bg-white hover:text-black transition-all duration-300 text-sm tracking-wide"
            >
              TRY AGAIN
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
             {/* Hero Section */}
       <section className="relative py-20 px-0 min-h-screen flex items-center">
        {homeSettings?.galleryHeroImage && (
          <div className="absolute inset-0 z-0">
                         <img
               src={getImageUrl(homeSettings.galleryHeroImage)}
               alt="Gallery Hero Background"
               className="w-full h-full object-cover md:object-cover object-contain"
               onError={(e) => {
                 const target = e.target as HTMLImageElement;
                 target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
               }}
             />
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
        )}
        <div className="relative z-10 w-full">
          <div className="text-center">
            <h1 className="text-6xl md:text-8xl font-light mb-6 tracking-wider" style={{ fontFamily: 'FuturaLTPaneuropean' }}>
              Studio Gallery
            </h1>
                         <p className="text-xl md:text-2xl text-gray-300 mb-12 font-light max-w-3xl mx-auto" style={{ fontFamily: 'Montserrat-Light' }}>
               Burası bizim dünyamız… Stüdyomuzun sıcak havasını, emeğimizi ve sizler için hazırladığımız güzellikleri bu karelerde görebilirsiniz.
               <br /><br />
               Hayal ettiğiniz dokunuşu, bizim gözümüzden keşfedin!
             </p>
          </div>
        </div>
      </section>



      {/* Gallery Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          {galleryItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-6">🎨</div>
              <h3 className="text-2xl font-light mb-4">Bu kategoride resim bulunamadı</h3>
                              <p className="text-gray-400">Lütfen diğer kategorileri kontrol edin veya daha fazla bilgi için bizimle iletişime geçin</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {galleryItems.map((item, index) => (
                <div
                  key={item.id}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(index)}
                >
                  <div className="aspect-square bg-gray-900 border border-gray-800 hover:border-gray-600 transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                    <img
                      src={getImageUrl(item.imageUrl)}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <ArrowRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="py-12 px-4 border-t border-gray-800">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    fetchGalleryImages(currentPage - 1);
                  }
                }}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-600 text-gray-400 hover:border-gray-300 hover:text-gray-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
              >
                GERİ
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => fetchGalleryImages(page)}
                  className={`px-4 py-2 border transition-all duration-300 text-sm tracking-wide ${
                    currentPage === page
                      ? 'border-white text-white bg-white/10'
                      : 'border-gray-600 text-gray-400 hover:border-gray-300 hover:text-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => {
                  if (currentPage < totalPages) {
                    fetchGalleryImages(currentPage + 1);
                  }
                }}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-600 text-gray-400 hover:border-gray-300 hover:text-gray-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
              >
                İLERİ
              </button>
            </div>
          </div>
        </section>
      )}

      

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>

                    {/* Modal */}
       {selectedImage !== null && galleryItems[selectedImage] && (
         <div className="fixed inset-0 flex items-center justify-center z-50 p-4 md:p-4" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
           <div className="absolute inset-0 bg-black bg-opacity-90"></div>
           <div className="relative w-full h-full md:max-w-4xl md:h-auto">
             <button
               onClick={() => setSelectedImage(null)}
               className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
             >
               <X className="w-8 h-8" />
             </button>
             
             {/* Previous Button */}
             {selectedImage > 0 && (
               <button
                 onClick={() => setSelectedImage(selectedImage - 1)}
                 className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
               >
                 <ChevronLeft className="w-8 h-8" />
               </button>
             )}
             
             {/* Next Button */}
             {selectedImage < galleryItems.length - 1 && (
               <button
                 onClick={() => setSelectedImage(selectedImage + 1)}
                 className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
               >
                 <ChevronRight className="w-8 h-8" />
               </button>
             )}
             
             <img
               src={getImageUrl(galleryItems[selectedImage].imageUrl)}
               alt={galleryItems[selectedImage].title}
               className="w-full h-full md:max-h-96 object-contain md:object-contain object-cover rounded-lg"
               onError={(e) => {
                 const target = e.target as HTMLImageElement;
                 target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
               }}
             />
           </div>
         </div>
       )}
    </div>
  );
};

export default Gallery;